import React from 'react';
import { CONJUNCTION_TABLE_CONTEXT } from '@/constants/context';
import ConjunctionTable from '@/components/astroOne/conjunctionTable/body/ConjunctionTable';
import { SearchType } from '@/components/astroOne/conjunctionTable/header/Search';
import PageTemplate from '../../components/ui/PageTemplate';
import DCAStatsBoxes from '@/components/conjunction/DCAStatsBoxes';
import ConjunctionCardList from '@/components/conjunction/ConjunctionCardList';
import useObserver from '@/hooks/page/useObserver';
import { useConjunctionTable } from '@/components/astroOne/conjunctionTable/body/conjunctionTableHooks';
import Pagination from '@/components/ui/table/Pagination';
import ConjunctionSelect from '@/components/core/select/ConjunctionSelect';
import { useDCAStats } from '@/hooks/conjunctions/useDCAStats';

const MOBILE_LIMIT = 5;
const LIMIT = 10;
const MOBILE_PAGE = 20;
const PAGE = 10;

export const ConjunctionBriefingContent = () => {
  useObserver(
    'content-ConjunctionBriefing',
    'content-ConjunctionBriefing-tablet',
    'content-ConjunctionBriefing-desktop'
  );

  const { stats } = useDCAStats();
  const dcaStats = stats ?? { dca1km: 0, dca5km: 0, dca10km: 0 };

  const {
    selected,
    setSelected,
    page,
    setPage,
    isLoading,
    isFetching,
    tableData,
    startCollection,
    endCollection,
  } = useConjunctionTable({ limit: MOBILE_LIMIT, queryKey: SearchType.SEARCH });

  return (
    <>
      {/* Mobile Layout */}
      <div
        id='content-ConjunctionBriefing'
        className='md:hidden max-w-[360px] mx-auto w-full py-20 flex flex-col gap-10'
      >
        <div className='text-center'>
          <p className='text-[28px] font-semibold text-white'>
            Conjunction Briefing
          </p>
        </div>
        <div className='flex flex-col gap-8 px-4'>
          <DCAStatsBoxes stats={dcaStats} />

          {/* Title and Select */}
          <div className='flex flex-col gap-2'>
            <div className='flex items-start gap-2'>
              <div className='flex flex-col'>
                <div className='flex flex-col gap-1'>
                  <h3 className='text-sm font-semibold text-[#A7A7A7]'>
                    {CONJUNCTION_TABLE_CONTEXT.TITLE}
                  </h3>
                  <div className='text-xs font-medium text-[#6C6C6C]'>
                    {CONJUNCTION_TABLE_CONTEXT.DESCRIPTION}
                  </div>
                  <div>
                    <p className='text-xs font-medium text-[#6C6C6C]'>
                      Prediction Window :
                    </p>
                    <p className='text-xs font-medium text-[#6C6C6C]'>
                      {startCollection} ~ {endCollection} (UTC)
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <ConjunctionSelect
                  selected={selected}
                  setSelected={setSelected}
                  type='HOME'
                />
            <div className='w-full flex flex-col gap-1 border border-[#FCCB164D] rounded-lg p-1 bg-black/50'>
              {/* Card List */}
              <ConjunctionCardList
                data={tableData}
                page={page}
                limit={MOBILE_LIMIT}
                isLoading={isLoading || isFetching}
              />

              {/* Pagination */}
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={MOBILE_PAGE}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tablet Layout */}
      <div
        id='content-ConjunctionBriefing-tablet'
        className='hidden md:flex xl:hidden max-w-[688px] mx-auto w-full h-full flex-col justify-center items-center py-36 gap-14'
      >
        <div>
          <p className='text-4xl font-semibold text-white'>
            Conjunction Briefing
          </p>
        </div>
        <div className='flex flex-col w-full gap-8'>
          <DCAStatsBoxes stats={dcaStats} />
          <div className='w-[100%] py-2 bg-black/50 rounded-[10px]'>
            <ConjunctionTable
              queryKey={SearchType.SEARCH}
              limit={LIMIT}
              totalPage={PAGE}
              title={CONJUNCTION_TABLE_CONTEXT.TITLE}
              description={CONJUNCTION_TABLE_CONTEXT.DESCRIPTION}
            />
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div
        id='content-ConjunctionBriefing-desktop'
        className='hidden xl:flex w-full max-w-[1060px] mx-auto h-full flex-col justify-center items-center py-36 gap-16'
      >
        <div>
          <p className='text-4xl font-semibold text-white'>
            Conjunction Briefing
          </p>
        </div>
        <div className='flex flex-col w-full gap-6'>
          <DCAStatsBoxes stats={dcaStats} />
          <div className='w-[100%] bg-black/50 rounded-[10px]'>
            <ConjunctionTable
              queryKey={SearchType.SEARCH}
              limit={LIMIT}
              totalPage={PAGE}
              title={CONJUNCTION_TABLE_CONTEXT.TITLE}
              description={CONJUNCTION_TABLE_CONTEXT.DESCRIPTION}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Original component with PageTemplate wrapper
const ConjunctionBriefing = () => {
  return (
    <PageTemplate id='Conjunction Briefing'>
      <ConjunctionBriefingContent />
    </PageTemplate>
  );
};

export default ConjunctionBriefing;
